import React, {useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from 'axios';
import moment from 'moment';

import { Paper } from '@material-ui/core';
import MaterialTable from 'material-table';

export const CompetitionTable = (props) => {

	// Store GRØD api URL
	const api_url = process.env.REACT_APP_GROED_API_URL;

	// Variables from Auth0
	const { getAccessTokenSilently } = useAuth0();

	// Loading state
	const [isLoading, setIsLoading] = useState(true);

	// Loading state
	const [widgetState, setWidgetState] = useState({
		error: false,
		table_data: [],
		products_by_department: [],
		revenue_by_department: [],
		categories_by_department: [],
		staff_food_by_department: {},
		waste_by_department: {},
	});

	/**
	 * Set frequency options
	 */
	useEffect(() => {
		loadAllData();	
	}, [
		props.start_date,
		props.end_date,
		props.department_ids,
		props.departments
	]);


	useEffect(() => {
		formatTableData();
	}, [widgetState.products_by_department]);

	/**
	 * Handle data load
	 */
	const loadAllData = () => {
		
		setIsLoading(true);


		const aggregatedProductsByDepartmentPromise = loadAggregatedProductsByDepartment();

		const loadAggregatedCategoriesByDepartmentPromise = loadAggregatedCategoriesByDepartment();

		const loadAggregatedDepartmentDataPromis = loadAggregatedDepartmentData();

		const deductionsPromise = loadDeductionsByDepartment();

		Promise.all([aggregatedProductsByDepartmentPromise, loadAggregatedCategoriesByDepartmentPromise, loadAggregatedDepartmentDataPromis, deductionsPromise])
			.then( values => {

				// console.log(values[0].data.products_by_department);
				// console.log(values[2].data.departments);
				// console.log(values[1].data.categories_by_department);

				setWidgetState({
					...widgetState,
					error:  false,
					products_by_department: values[0].data.products_by_department,
					revenue_by_department: values[2].data.departments,
					categories_by_department: values[1].data.categories_by_department,
					staff_food_by_department: values[3].staff_food_by_department,
					waste_by_department: values[3].waste_by_department,
				});

				setIsLoading(false);


			})
			.catch(error => {
				setWidgetState({
					...widgetState,
					error:  true,
					products_by_department: [],
					revenue_by_department: [],
					categories_by_department: [],
					staff_food_by_department: {},
					waste_by_department: {},
				});

				setIsLoading(false);
				console.log(error);
			}
		);
	}




	/**
	 * Load sales data
	 */
	const loadAggregatedProductsByDepartment = async () => {
		// Get access token
		const access_token = await getAccessTokenSilently();

		// Get departments data
		const axiosPromise = await axios.get(`${api_url}/aggregation/products-by-departments/`, { 
			headers: { 'Authorization': `Bearer ${access_token}`},
			params: {
                start_date: moment(props.start_date).format('YYYY-MM-DD 00:00'),
                end_date: moment(props.end_date).format('YYYY-MM-DD 23:59'),
                product_name: [
                	// 'Surdejsbrød',
                	// 'Surdejsbolle',
                	// 'Surdejsbolle med smør',
                	// 'Surdejsbolle uden smør',
                	// 'Grødbolle med smør og ost',
                	// 'Grødbolle med smør og ost TO GO',
                	// 'Grødbolle UDEN smør',
                	// 'Grødbolle UDEN smør TO GO',
                	// 'Grødbolle med smør',
                	// 'Grødbolle med smør TO GO',
                	// 'Grødbolle med vegansk smør',
                	// 'Grødbolle med vegansk smør TO GO',
                	// 'Grødbolle med vegansk smør og ost',
                	// 'Grødbolle med vegansk smør og ost TO GO',
                	// 'Kogebog - engelsk',
                	// 'Kogebog - dansk',
                	// 'Kogebogs tilbud DK',
                	// 'Kogebogs tilbud ENG',
                	// 'Kogebog og Risengrød',
                	// 'Kogebog og Risengrød TO GO',
                	// 'Chiagrød',
                	'Jordbær'
                ],
                product_categories: [
                	'Toppings - Morgen',
                	'Toppings - Morgen (ekstra)'
                ],
			}
		});

		return axiosPromise;
	}



	/**
	 * Sum the item_count of every "Jordbær" product in an aggregation response.
	 * (There are multiple "Jordbær" products, one per topping category.)
	 */
	const sumJordbaer = (products) => (products || [])
		.filter(product => product.product_name === 'Jordbær')
		.reduce((sum, product) => sum + product.item_count, 0);

	/**
	 * Fetch per-department "Jordbær" counts for a given aggregation endpoint
	 * (e.g. employee-products / waste-products), keyed by department_name.
	 *
	 * These endpoints only return totals aggregated across the department_ids
	 * passed in, so we query them once per department.
	 */
	const loadJordbaerByDepartment = async (endpoint, access_token, departments) => {
		const results = await Promise.all(departments.map(async (dep) => {
			try {
				const res = await axios.get(`${api_url}/aggregation/${endpoint}`, {
					headers: { 'Authorization': `Bearer ${access_token}`},
					params: {
						department_ids: [dep.department_id],
						start_date: moment(props.start_date).format('YYYY-MM-DD 00:00'),
						end_date: moment(props.end_date).format('YYYY-MM-DD 23:59'),
					}
				});

				return { name: dep.department_name, count: sumJordbaer(res.data.products) };
			} catch (err) {
				console.log(`[CompetitionTable] ${endpoint} failed for "${dep.department_name}":`, err.response ? err.response.status : err.message);
				return { name: dep.department_name, count: 0 };
			}
		}));

		return results.reduce((acc, result) => {
			acc[result.name] = result.count;
			return acc;
		}, {});
	}

	/**
	 * Load the per-department "Jordbær" counts that should NOT count toward the
	 * competition: staff food (employee-products) and waste (waste-products).
	 *
	 * Returns { staff_food_by_department, waste_by_department }, keyed by
	 * department_name. On any failure we fall back to empty maps (no subtraction)
	 * so the leaderboard is never broken.
	 */
	const loadDeductionsByDepartment = async () => {
		try {

			// Get access token
			const access_token = await getAccessTokenSilently();

			// The /departments/ endpoint is currently unreliable (500s), so derive the
			// department list from /aggregation/department-data/ which the leaderboard
			// already loads successfully.
			const departmentsRes = await axios.get(`${api_url}/aggregation/department-data/`, {
				headers: { 'Authorization': `Bearer ${access_token}`},
				params: {
					start_date: moment(props.start_date).format('YYYY-MM-DD 00:00'),
					end_date: moment(props.end_date).format('YYYY-MM-DD 23:59'),
				}
			});

			const departments = (departmentsRes.data.departments || []).filter(dep => dep.department_id);

			const [staff_food_by_department, waste_by_department] = await Promise.all([
				loadJordbaerByDepartment('employee-products', access_token, departments),
				loadJordbaerByDepartment('waste-products', access_token, departments),
			]);

			return { staff_food_by_department, waste_by_department };

		} catch (err) {
			console.log('[CompetitionTable] loadDeductionsByDepartment failed:', err.response ? err.response.status : err.message);
			return { staff_food_by_department: {}, waste_by_department: {} };
		}
	}



	/**
	 * Load Aggregated revenue by department
	 */
	const loadAggregatedRevenueByDepartment = async () => {

		// Get access token
		const access_token = await getAccessTokenSilently();

		// Get departments data
		const axiosPromise = await axios.get(`${api_url}/aggregation/revenue-by-departments/`, { 
			headers: { 'Authorization': `Bearer ${access_token}`},
			params: {
                start_date: moment(props.start_date).format('YYYY-MM-DD 00:00'),
                end_date: moment(props.end_date).format('YYYY-MM-DD 23:59'),
			}
		});

		return axiosPromise;
	}


	/**
	 * Load sales data
	 */
	const loadAggregatedDepartmentData = async () => {

		// Get access token
		const access_token = await getAccessTokenSilently();

		// Get departments data
		const axiosPromise = await axios.get(`${api_url}/aggregation/department-data/`, { 
			headers: { 'Authorization': `Bearer ${access_token}`},
			params: {
                start_date: moment(props.start_date).format('YYYY-MM-DD 00:00'),
                end_date: moment(props.end_date).format('YYYY-MM-DD 23:59'),
			}
		});

		return axiosPromise;
	}

	/**
	 * Load Aggregated categories by department
	 */
	const loadAggregatedCategoriesByDepartment = async () => {


		// Get access token
		const access_token = await getAccessTokenSilently();

		// Get departments data
		const axiosPromise = await axios.get(`${api_url}/aggregation/categories-by-departments/`, { 
			headers: { 'Authorization': `Bearer ${access_token}`},
			params: {
                start_date: moment(props.start_date).format('YYYY-MM-DD 00:00'),
                end_date: moment(props.end_date).format('YYYY-MM-DD 23:59'),
			}
		});

		return axiosPromise;

	}



	/**
	 * Format table data
	 */
	const formatTableData = () => {

		const products_by_department = widgetState.products_by_department;
        const revenue_by_department = widgetState.revenue_by_department;
        const categories_by_department = widgetState.categories_by_department;
                	

        console.log(categories_by_department);

        // const products = products_by_department.filter(product => product.product_name === 'Jordbær');
        const products = products_by_department;


        let table_data = [];

        for (const product of products) {

            for (const department of product.sales) {
                    
                const department_order_count = revenue_by_department.find(dep => dep.department_name === department.department_name);


                const category_morgen = categories_by_department.find(cat => cat.category_name === 'Morgen' );
                const category_morgen_item_count = category_morgen.sales.find(dep => dep.department_name === department.department_name).item_count;
                const category_morgen_togo = categories_by_department.find(cat => cat.category_name === 'Morgen TO GO' );
                const category_morgen_togo_item_count = category_morgen.sales.find(dep => dep.department_name === department.department_name).item_count;

                const category_item_count = category_morgen_item_count + category_morgen_togo_item_count;

                console.log(category_item_count);

                //const category_item_count = categories_by_department.find(dep => dep.department_name === department.department_name && );


                const index = table_data.findIndex(row => row.department === department.department_name);

                let row;

                if (index === -1) {
            	    row = {
                        department: department.department_name, 
                        count: department.item_count, 
                        //order_count: department_order_count.onlinepos.order_count,
                        order_count: category_item_count,
                        revenue: (department.revenue_excl_vat / 100).toFixed(0),
                        share: parseFloat((department_order_count.onlinepos.order_count / department.item_count) * 100).toFixed(2),
                        index: 0,
                    }

                    table_data.push(row);
                    row.index = parseFloat(row.count / row.order_count * 10000).toFixed(1);

                } else {
                        
                    table_data[index] = {
                            department: department.department_name,
                            count: parseInt(department.item_count + table_data[index].count),
                            order_count: parseInt(category_item_count + table_data[index].order_count),
                            revenue: parseInt(department.revenue_excl_vat / 100) + parseInt(table_data[index].revenue),
                            share: parseFloat(((parseInt(department.item_count + table_data[index].count)) / table_data[index].order_count) * 100).toFixed(2),
                            index: 0,
                    }

                    table_data[index].index = parseFloat(table_data[index].count / table_data[index].order_count * 10000).toFixed(1);

                }

            }

        }


        // Subtract staff-food ("Medarbejdermad") Jordbær per department once,
        // then recompute the index off the corrected count.
        const staff_food_by_department = widgetState.staff_food_by_department;
        const waste_by_department = widgetState.waste_by_department;

        table_data = table_data.map(row => {
            const staff_food = staff_food_by_department[row.department] || 0;
            const waste = waste_by_department[row.department] || 0;
            row.staff_food = staff_food;
            row.waste = waste;
            row.count = Math.max(0, parseInt(row.count) - staff_food - waste);
            row.index = parseFloat(row.count / row.order_count * 10000).toFixed(1);
            return row;
        });


        // Sort array of objects
        table_data.sort((a, b) => (parseFloat(a.index) > parseFloat(b.index) ) ? -1 : 1);

        let count = 1;

        table_data.map(item => {
        	item.place = '#' + count;
        	count++;
        	return item;
        })

        setWidgetState({ 
			...widgetState,
			table_data: table_data,
		});


	}


	let loadingClass = "widget--loading";

	if (isLoading) {
		loadingClass = "widget--loading";			
	} else {
		loadingClass = "";
	}

	return (
		<div className={`widget-v2 ${loadingClass}`}>
		    <div className="widget-v2-header">
		    	<h2 className="widget-v2-title">Leaderboard - Jordbær pt. 2!</h2>
		    </div>
			
			<div className="widget-v2-body">
					{
						widgetState.error && (
							<p className="responsive-line-error">Wups! Der skete en fejl! Send Malthe et screenshot ❤️</p>
						)
					}

					{
						isLoading === true ? (
							<div className="responsive-line-container">
								<div className="spinner-centered">
									<div className="spinner"></div>
								</div>
							</div>
						) : (
							<MaterialTable
								title="Leaderboard"
								columns={[
                                    { title: 'Afdeling', field: 'department' },
                                    { title: 'Antal solgte', field: 'count', type: 'numeric' },
                                    { title: 'Medarbejdermad', field: 'staff_food', type: 'numeric', hidden: true },
                                    { title: 'Spild', field: 'waste', type: 'numeric', hidden: true },
                                    { title: 'Antal Morgen items', field: 'order_count', type: 'numeric' },
                                    //{ title: 'Omsætning', field: 'revenue', type: 'numeric' },
                                    //{ title: 'Index', field: 'index', type: 'numeric' },
                                    //{ title: 'Share %', field: 'share', type: 'numeric' },
                                    { title: 'Placement', field: 'place', type: 'numeric' },
                                ]}
								data={widgetState.table_data}
								components={{ Container: props => <Paper {...props} elevation={0}/> }} // This removes the drop shadow 
								options={{
										search: false,
										pagination: false,
										pageSize: 10,
										pageSizeOptions: [5, 9, 10, 20],
										//exportButton: true,
										//filtering: true,
										toolbar: false,
								}}

								/>
						)
					}
					
			</div>						        

			{/*
		    <div className="widget-v2-footer">
		    	footer
		    </div>
		    */}

		</div>
	); 
};

export default CompetitionTable;
